export const EARLIEST_BUSINESS_DATE = "0001-01-01";
// Inclusive DATERANGEs are canonicalized to an exclusive upper bound. Keeping
// one day below Python's maximum date prevents PostgreSQL/psycopg2 from returning the
// unrepresentable 10000-01-01 upper bound through psycopg2.
export const LATEST_BUSINESS_DATE = "9999-12-30";

export const businessValidityBoundaryDate = (boundary) =>
  boundary === "start" ? EARLIEST_BUSINESS_DATE : LATEST_BUSINESS_DATE;

export const clampBusinessValidityDate = (boundary, value) =>
  boundary === "end" && value > LATEST_BUSINESS_DATE ? LATEST_BUSINESS_DATE : value;

const shiftIsoDate = (value, days) => {
  const shifted = new Date(`${value}T00:00:00Z`);
  shifted.setUTCDate(shifted.getUTCDate() + days);
  return shifted.toISOString().slice(0, 10);
};

export const findBusinessValidityGaps = (versions = []) => {
  const ordered = versions
    .filter((version) => version.start_date)
    .map((version) => ({
      ...version,
      start_date: version.start_date,
      end_date: version.end_date || null,
    }))
    .sort((a, b) => a.start_date.localeCompare(b.start_date));

  const gaps = [];
  for (let index = 1; index < ordered.length; index += 1) {
    const previous = ordered[index - 1];
    const next = ordered[index];
    if (previous.end_date) {
      const gapStart = shiftIsoDate(previous.end_date, 1);
      const gapEnd = shiftIsoDate(next.start_date, -1);
      if (gapStart <= gapEnd) {
        gaps.push({ start_date: gapStart, end_date: gapEnd });
      }
    }
  }
  return gaps;
};

export const lisbonToday = () => {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Europe/Lisbon",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date());
  const byType = Object.fromEntries(parts.map(({ type, value }) => [type, value]));
  return `${byType.year}-${byType.month}-${byType.day}`;
};

export const defaultBusinessEndDate = (startDate = lisbonToday(), years = 3) => {
  const [year, month, day] = startDate.split("-").map(Number);
  if (!year || !month || !day) return "";
  const endExclusive = new Date(Date.UTC(year + years, month - 1, day));
  endExclusive.setUTCDate(endExclusive.getUTCDate() - 1);
  return endExclusive.toISOString().slice(0, 10);
};

export const withBusinessValidity = (value = {}) => ({
  ...value,
  start_date: value.start_date ?? value.validity ?? lisbonToday(),
  end_date: value.end_date ?? null,
});

export const effectiveAtQuery = (value) =>
  `effective_at=${encodeURIComponent(value ?? lisbonToday())}`;

const normalizedValidityDate = (value) => value || "";

export const hasValidityPeriodChanged = ({
  originalStartDate,
  originalEndDate,
  startDate,
  endDate,
}) =>
  normalizedValidityDate(originalStartDate) !== normalizedValidityDate(startDate) ||
  normalizedValidityDate(originalEndDate) !== normalizedValidityDate(endDate);

const resourceDetailFields = {
  resource_iu_versions: {
    sum_certified_power: "sumCertifiedPower",
    ic_voltage_level: "voltageLevel",
  },
  resource_ic_versions: {
    sum_certified_power: "sumCertifiedPower",
    ic_voltage_level: "voltageLevel",
  },
  resource_scpu_versions: { generation_capacity: "generationCapacity" },
  resource_storage_versions: {
    nominal_capacity: "nominalCapacity",
    max_power: "maxPower",
    charging_efficiency: "chargingEfficiency",
    discharging_efficiency: "dischargingEfficiency",
    min_SOC: "minSOC",
    max_SOC: "maxSOC",
    deg_cost: "degCost",
    init_soc: "initSoc",
  },
  resource_ev_versions: {
    nominal_capacity: "nominalCapacity",
    max_power_input: "maxPowerInput",
    max_power_output: "maxPowerOutput",
    charging_efficiency: "chargingEfficiency",
    discharging_efficiency: "dischargingEfficiency",
    min_SOC: "minSOC",
    max_SOC: "maxSOC",
    desired_min_SOC: "desiredMinSOC",
    deg_cost: "degCost",
    init_soc: "initSoc",
  },
  resource_ewh_versions: {
    capacity: "capacity",
    power: "power",
    max_temp: "maxTemp",
    target_temp: "targetTemp",
    default_start_temp: "defaultStartTemp",
  },
  resource_hp_versions: {
    power_rated: "powerRated",
    capacity_tank: "capacityTank",
    temp_inlet: "tempInlet",
    temp_desired: "tempDesired",
    temp_out_init: "tempOutInit",
    temp_indoor_init: "tempIndoorInit",
    temp_indoor_final: "tempIndoorFinal",
    temp_indoor_min: "tempIndoorMin",
    temp_indoor_max: "tempIndoorMax",
    temp_out_min: "tempOutMin",
    temp_out_max: "tempOutMax",
    u_value: "uValue",
    thermal_resistance: "thermalResistance",
    h_rad: "hRad",
    area_rad: "areaRad",
  },
};

const indexBy = (rows = [], key) => new Map(rows.map((row) => [String(row[key]), row]));

const contractPayload = (data, contractId, side) => {
  const expectedSide = `CONTRACT_${side.toUpperCase()}_VALUES`;
  const tables = data.contract_tariff_details ?? {};
  return Object.values(tables)
    .flat()
    .find(
      (row) =>
        String(row.contract_id) === String(contractId) && row.buy_or_sell_type === expectedSide
    );
};

/** Convert a canonical 2.0 snapshot into the simulator's editable import shape. */
export const canonicalSnapshotToSimulator = (data, importVersion) => {
  const config = data.community_config_versions?.[0];
  if (!config) return null;

  const membershipById = indexBy(data.membership_versions, "membership_id");
  const meterVersionById = indexBy(data.meter_versions, "meter_id");
  const resourceVersionById = indexBy(data.resource_versions, "resource_id");
  const respVersionById = indexBy(data.resp_versions, "resp_id");
  const fixedVersionById = indexBy(data.fixed_bilateral_versions, "relationship_id");

  const members = (data.memberships ?? []).flatMap((membership) => {
    const version = membershipById.get(String(membership.id));
    if (!version) return [];
    return [
      {
        pre_id: membership.user_ref,
        name: membership.user?.name ?? "",
        is_host: version.is_host,
        is_promoter: version.is_promoter,
        start_date: version.start_date,
        end_date: version.end_date,
      },
    ];
  });

  const meters = (data.meters ?? []).flatMap((meter) => {
    const version = meterVersionById.get(String(meter.id));
    if (!version) return [];
    return [
      {
        pre_id: meter.id,
        name: meter.identifier,
        type: meter.type,
        cpe: version.cpe_id ?? "",
        installation_type: version.installation_type,
        production_identifier: version.production_identifier ?? "",
        start_date: version.start_date,
        end_date: version.end_date,
      },
    ];
  });

  const resources = (data.resources ?? []).flatMap((resource) => {
    const version = resourceVersionById.get(String(resource.id));
    if (!version) return [];
    const details = {};
    Object.entries(data.resource_detail_versions ?? {}).forEach(([table, rows]) => {
      const row = (rows ?? []).find(
        (item) => String(item.resource_version_id) === String(version.id)
      );
      Object.entries(resourceDetailFields[table] ?? {}).forEach(([source, target]) => {
        if (row?.[source] !== undefined) details[target] = row[source];
      });
    });
    return [
      {
        pre_id: resource.id,
        name: version.name,
        type: resource.type,
        meter: version.meter_id ?? "",
        start_date: version.start_date,
        end_date: version.end_date,
        ...details,
        ownerships: (data.ownership_versions ?? [])
          .filter((row) => String(row.resource_id) === String(resource.id))
          .map((row) => ({
            member: row.user_id,
            perc: row.perc_ownership,
            start_date: row.start_date,
            end_date: row.end_date,
          })),
      },
    ];
  });

  const contractsByMeter = indexBy(data.contracts, "meter_id");
  const contracts = meters
    .filter((meter) => meter.type === "CPE" || meter.type === "AGGREGATOR_METER")
    .flatMap((meter) => {
      const contract = contractsByMeter.get(String(meter.pre_id));
      if (!contract) return [];
      return [
        {
          ...contract,
          member: contract.user_id,
          meter: contract.meter_id,
          buy_payload: contractPayload(data, contract.id, "buy") ?? null,
          sell_payload: contractPayload(data, contract.id, "sell") ?? null,
        },
      ];
    });

  const seenRespPairs = new Set();
  const resp = (data.resp ?? []).flatMap((relationship) => {
    const pairKey = [relationship.provider_meter_id, relationship.receiver_meter_id]
      .map(String)
      .sort()
      .join(":");
    const version = respVersionById.get(String(relationship.id));
    if (!version || seenRespPairs.has(pairKey)) return [];
    seenRespPairs.add(pairKey);
    return [
      {
        provider_meter_id: relationship.provider_meter_id,
        receiver_meter_id: relationship.receiver_meter_id,
        is_active: version.is_active,
        start_date: version.start_date,
        end_date: version.end_date,
      },
    ];
  });

  return {
    import_version: importVersion,
    basicInfo: {
      community_name: data.community?.name ?? "",
      community_identifier: data.community?.identifier ?? "",
      allocation: config.method_alloc_coeff,
      community_or_aggregator_type: config.community_or_aggregator_type,
      market_rate: config.market_rate,
      market_divisor: config.market_divisor,
      market_compensation: config.market_compensation,
      market_small_increment: config.market_small_increment,
      market_fixed_price: config.market_fixed_price,
      start_date: config.start_date,
      end_date: config.end_date,
    },
    members,
    meters,
    resources,
    contracts,
    resp,
    fixedBilateralPrices: (data.fixed_bilateral_relationships ?? []).flatMap((relationship) => {
      const version = fixedVersionById.get(String(relationship.id));
      if (!version) return [];
      return [
        {
          provider_user_id: relationship.provider_user_id,
          receiver_user_id: relationship.receiver_user_id,
          price: version.price,
          start_date: version.start_date,
          end_date: version.end_date,
        },
      ];
    }),
  };
};
